import React, { createContext, useContext, useState, useEffect } from "react";
import { useMediaQuery } from "@mui/material";

type ThemeMode = "dark" | "light";
export type ThemePreference = ThemeMode | "system";

interface ThemeModeContextValue {
  mode: ThemeMode;
  preference: ThemePreference;
  setPreference: (preference: ThemePreference) => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue>({
  mode: "dark",
  preference: "system",
  setPreference: () => {}
});

const STORAGE_KEY = "scottdb-theme-mode";

function getInitialPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch {}
  return "system";
}

export function ThemeModeProvider({ children }: { children: React.ReactNode }) {
  const [preference, setPreference] =
    useState<ThemePreference>(getInitialPreference);
  const systemDark = useMediaQuery("(prefers-color-scheme: dark)");
  const mode: ThemeMode =
    preference === "system" ? (systemDark ? "dark" : "light") : preference;

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, preference);
    } catch {}
    document.documentElement.dataset.theme = mode;
    document.documentElement.style.colorScheme = mode;
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute("content", mode === "dark" ? "#0a0a0f" : "#f5f5fa");
  }, [mode, preference]);

  return (
    <ThemeModeContext.Provider value={{ mode, preference, setPreference }}>
      {children}
    </ThemeModeContext.Provider>
  );
}

export function useThemeMode() {
  return useContext(ThemeModeContext);
}
